/**
 * Audio Capture Service
 *
 * Handles microphone access, AudioContext setup, and real-time PCM extraction
 * via an AudioWorklet. Provides audio level metering for UI visualization.
 *
 * Usage:
 *   const capture = new AudioCaptureService()
 *   capture.onAudioChunk = (pcm) => sttProvider.sendAudio(pcm)
 *   await capture.initialize()
 *   // ... user speaks ...
 *   capture.destroy()
 */

export class AudioCaptureService {
  private audioContext: AudioContext | null = null
  private mediaStream: MediaStream | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null
  private workletNode: AudioWorkletNode | null = null
  private analyserNode: AnalyserNode | null = null
  private _initialized = false

  /** Callback for PCM audio chunks (Int16Array as ArrayBuffer) */
  onAudioChunk?: (pcmData: ArrayBuffer) => void

  /** Callback for audio level (0-1) for visualization */
  onAudioLevel?: (level: number) => void

  private levelIntervalId: ReturnType<typeof setInterval> | null = null

  get initialized(): boolean {
    return this._initialized
  }

  get sampleRate(): number {
    return this.audioContext?.sampleRate ?? 16000
  }

  /** Expose the MediaStream for sharing with other services (e.g., SileroVAD) */
  get stream(): MediaStream | null {
    return this.mediaStream
  }

  /**
   * Initialize microphone access and AudioWorklet pipeline.
   * Must be called in response to a user gesture (browser requirement).
   */
  async initialize(): Promise<void> {
    if (this._initialized) return

    // Request microphone access with audio processing constraints
    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: 16000,
        channelCount: 1,
      },
    })

    // Create AudioContext at 16kHz (Deepgram's expected sample rate)
    this.audioContext = new AudioContext({ sampleRate: 16000 })

    // Register AudioWorklet processor for real-time PCM extraction
    await this.audioContext.audioWorklet.addModule('/pcm-processor.js')

    // Create source from microphone
    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)

    // Create AudioWorklet for PCM extraction
    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-processor')
    this.workletNode.port.onmessage = (event: MessageEvent) => {
      // event.data contains Int16Array PCM samples
      this.onAudioChunk?.(event.data.buffer)
    }

    // Create AnalyserNode for audio level visualization
    this.analyserNode = this.audioContext.createAnalyser()
    this.analyserNode.fftSize = 256

    // Connect: Mic → Analyser → AudioWorklet
    this.sourceNode.connect(this.analyserNode)
    this.analyserNode.connect(this.workletNode)

    // Start audio level polling (30fps)
    this.startLevelPolling()

    this._initialized = true
  }

  /** Get current audio level (0-1) for visualization */
  getAudioLevel(): number {
    if (!this.analyserNode) return 0
    const dataArray = new Uint8Array(this.analyserNode.frequencyBinCount)
    this.analyserNode.getByteFrequencyData(dataArray)
    const average =
      dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length
    return average / 255
  }

  /** Suspend audio capture (keeps mic stream alive for quick resume) */
  async suspend(): Promise<void> {
    if (this.audioContext?.state === 'running') {
      await this.audioContext.suspend()
    }
    this.stopLevelPolling()
  }

  /** Resume audio capture */
  async resume(): Promise<void> {
    if (this.audioContext?.state === 'suspended') {
      await this.audioContext.resume()
    }
    this.startLevelPolling()
  }

  /** Full cleanup — releases microphone */
  destroy(): void {
    this.stopLevelPolling()

    if (this.workletNode) {
      this.workletNode.port.onmessage = null
      this.workletNode.disconnect()
      this.workletNode = null
    }
    if (this.analyserNode) {
      this.analyserNode.disconnect()
      this.analyserNode = null
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop())
      this.mediaStream = null
    }
    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this._initialized = false
    this.onAudioChunk = undefined
    this.onAudioLevel = undefined
  }

  private startLevelPolling(): void {
    this.stopLevelPolling()
    this.levelIntervalId = setInterval(() => {
      const level = this.getAudioLevel()
      this.onAudioLevel?.(level)
    }, 33) // ~30fps
  }

  private stopLevelPolling(): void {
    if (this.levelIntervalId) {
      clearInterval(this.levelIntervalId)
      this.levelIntervalId = null
    }
  }
}
